import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import {Trophy, MapPin, Globe, TrendingUp, ArrowRight} from 'lucide-react'
import { lumi } from '../lib/lumi'
import { useAuth } from '../hooks/useAuth'

export function Ranking() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [userProfile, setUserProfile] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [cityRank, setCityRank] = useState<number | null>(null)
  const [stateRank, setStateRank] = useState<number | null>(null)
  const [nationalRank, setNationalRank] = useState<number | null>(null)

  useEffect(() => {
    const fetchUserRankings = async () => {
      if (!user) {
        setLoading(false)
        return
      }

      try {
        // Buscar perfil do usuário
        const { list: userProfiles } = await lumi.entities.profiles.list({
          filter: { userId: user.userId }
        })
        
        if (userProfiles.length === 0) {
          setLoading(false)
          return
        }
        
        const profile = userProfiles[0]
        setUserProfile(profile)

        // Calcular posição municipal
        if (profile.city && profile.state) {
          const { list: cityDonors } = await lumi.entities.profiles.list({
            filter: { 
              isDonor: true,
              city: profile.city,
              state: profile.state
            },
            sort: { points: -1 },
            limit: 100
          })
          const cityPosition = cityDonors.findIndex(d => d._id === profile._id)
          setCityRank(cityPosition !== -1 ? cityPosition + 1 : null)
        }

        // Calcular posição estadual
        if (profile.state) {
          const { list: stateDonors } = await lumi.entities.profiles.list({
            filter: { 
              isDonor: true,
              state: profile.state
            },
            sort: { points: -1 },
            limit: 100
          })
          const statePosition = stateDonors.findIndex(d => d._id === profile._id)
          setStateRank(statePosition !== -1 ? statePosition + 1 : null)
        }

        // Calcular posição nacional
        const { list: nationalDonors } = await lumi.entities.profiles.list({
          filter: { isDonor: true },
          sort: { points: -1 },
          limit: 100
        })
        const nationalPosition = nationalDonors.findIndex(d => d._id === profile._id)
        setNationalRank(nationalPosition !== -1 ? nationalPosition + 1 : null)

      } catch (error) {
        console.error('Failed to fetch rankings:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchUserRankings()
  }, [user])

  const getRankDisplay = (rank: number | null) => {
    if (!rank) return '—'
    if (rank <= 3) return `🥇 #${rank}`
    if (rank <= 10) return `🏆 #${rank}`
    return `#${rank}`
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center shadow-lg">
              <Trophy className="h-10 w-10 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-black text-gray-900 mb-2">Rankings de Heróis</h1>
          <p className="text-gray-600 text-lg">
            Competição saudável e engajamento comunitário
          </p>
        </div>

        {/* User Position Summary */}
        {userProfile && (
          <Card className="mb-8 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300 shadow-xl">
            <CardContent className="pt-6">
              <div className="text-center mb-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center text-white font-black text-2xl shadow-lg mx-auto mb-3">
                  {userProfile.displayName?.charAt(0) || 'U'}
                </div>
                <h3 className="font-bold text-2xl text-gray-900">{userProfile.displayName}</h3>
                <p className="text-sm text-gray-600">{userProfile.bloodType} • {userProfile.city}/{userProfile.state}</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <h4 className="text-lg font-bold text-gray-900 mb-4 text-center">🏅 Suas Posições nos Rankings</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-red-50 to-white rounded-lg border border-red-200">
                    <div className="flex items-center space-x-3">
                      <MapPin className="h-5 w-5 text-red-600" />
                      <span className="font-medium text-gray-900">Minha Cidade</span>
                    </div>
                    <span className="text-xl font-black text-red-600">{getRankDisplay(cityRank)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-white rounded-lg border border-purple-200">
                    <div className="flex items-center space-x-3">
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                      <span className="font-medium text-gray-900">Meu Estado</span>
                    </div>
                    <span className="text-xl font-black text-purple-600">{getRankDisplay(stateRank)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-yellow-50 to-white rounded-lg border border-yellow-300">
                    <div className="flex items-center space-x-3">
                      <Globe className="h-5 w-5 text-yellow-600" />
                      <span className="font-medium text-gray-900">Brasil</span>
                    </div>
                    <span className="text-xl font-black text-yellow-600">{getRankDisplay(nationalRank)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ranking Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Municipal */}
          <Card 
            className="cursor-pointer hover:shadow-2xl transition-all hover:scale-105 bg-gradient-to-br from-red-50 to-white border-2 border-red-300"
            onClick={() => navigate('/ranking/municipal')}
          >
            <CardContent className="pt-6 text-center">
              <MapPin className="h-12 w-12 text-red-600 mx-auto mb-3" />
              <h3 className="text-xl font-black text-gray-900 mb-2">🏆 Minha Cidade</h3>
              <p className="text-sm text-gray-600 mb-4">Top 10 doadores do seu município</p>
              {cityRank && (
                <div className="bg-white rounded-lg p-3 border border-red-200 mb-3">
                  <p className="text-xs text-gray-600 mb-1">Você é</p>
                  <p className="text-2xl font-black text-red-600">{getRankDisplay(cityRank)}</p>
                </div>
              )}
              <Button variant="outline" className="w-full border-red-300 text-red-600 hover:bg-red-50">
                Ver Ranking <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* State */}
          <Card 
            className="cursor-pointer hover:shadow-2xl transition-all hover:scale-105 bg-gradient-to-br from-purple-50 to-white border-2 border-purple-300"
            onClick={() => navigate('/ranking/estadual')}
          >
            <CardContent className="pt-6 text-center">
              <TrendingUp className="h-12 w-12 text-purple-600 mx-auto mb-3" />
              <h3 className="text-xl font-black text-gray-900 mb-2">🌟 Meu Estado</h3>
              <p className="text-sm text-gray-600 mb-4">Top 20 doadores estaduais</p>
              {stateRank && (
                <div className="bg-white rounded-lg p-3 border border-purple-200 mb-3">
                  <p className="text-xs text-gray-600 mb-1">Você é</p>
                  <p className="text-2xl font-black text-purple-600">{getRankDisplay(stateRank)}</p>
                </div>
              )}
              <Button variant="outline" className="w-full border-purple-300 text-purple-600 hover:bg-purple-50">
                Ver Ranking <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* National */}
          <Card 
            className="cursor-pointer hover:shadow-2xl transition-all hover:scale-105 bg-gradient-to-br from-yellow-50 to-white border-2 border-yellow-300"
            onClick={() => navigate('/ranking/nacional')}
          >
            <CardContent className="pt-6 text-center">
              <Globe className="h-12 w-12 text-yellow-600 mx-auto mb-3" />
              <h3 className="text-xl font-black text-gray-900 mb-2">⭐ Brasil</h3>
              <p className="text-sm text-gray-600 mb-4">Top 50 lendas nacionais</p>
              {nationalRank && nationalRank <= 50 && (
                <div className="bg-white rounded-lg p-3 border border-yellow-300 mb-3">
                  <p className="text-xs text-gray-600 mb-1">Você é</p>
                  <p className="text-2xl font-black text-yellow-600">{getRankDisplay(nationalRank)}</p>
                </div>
              )}
              <Button variant="outline" className="w-full border-yellow-300 text-yellow-600 hover:bg-yellow-50">
                Ver Ranking <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Points System Info */}
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300">
          <CardHeader>
            <CardTitle className="text-2xl font-black text-center">💎 Sistema de Pontos Regionalizado</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🩸</span>
              </div>
              <div>
                <p className="font-bold text-gray-900">Doação Confirmada</p>
                <p className="text-sm text-gray-600">+100 pontos base</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🌟</span>
              </div>
              <div>
                <p className="font-bold text-gray-900">Primeira Doação no Município</p>
                <p className="text-sm text-gray-600">+50 pontos bônus</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🔥</span>
              </div>
              <div>
                <p className="font-bold text-gray-900">Sequência de 3 Doações (90 dias)</p>
                <p className="text-sm text-gray-600">+150 pontos bônus</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🤝</span>
              </div>
              <div>
                <p className="font-bold text-gray-900">Indicar Novo Doador da Cidade</p>
                <p className="text-sm text-gray-600">+75 pontos</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">⚡</span>
              </div>
              <div>
                <p className="font-bold text-gray-900">Doação em Município com Escassez Crítica</p>
                <p className="text-sm text-gray-600">+200 pontos (pontos em dobro!)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Button 
            onClick={() => navigate('/solicitacoes')}
            className="w-full max-w-md h-14 text-lg font-bold bg-red-600 hover:bg-red-700 text-white"
          >
            Ver Pedidos e Doar Agora
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  )
}
